import {
    Box,
    Checkbox,
    Divider,
    Flex,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { TbChevronDown, TbSearch } from 'react-icons/tb';
import { SettingsInlineEdit, SettingsOptionRow } from './primitives';
import { filterLocations, type LocationEntry } from '../../hooks/useGetLocations';

/** Above this many options an editor gets a search box. */
const SEARCH_THRESHOLD = 6;

export interface InlineCellOption {
    value: string;
    label: string;
}

/**
 * Cell content for an editable dropdown cell: the value as plain text with a
 * chevron that fades in while the row is hovered. Rows must set `role="group"`
 * for the chevron to appear.
 */
export const InlineCellDisplay = ({
    label,
    isMuted = false,
    hasChevron = true,
}: {
    label: string;
    isMuted?: boolean;
    hasChevron?: boolean;
}) => (
    <HStack spacing={2} justify="space-between" w="100%">
        <Text fontSize="sm" color={isMuted ? 'gray.400' : 'gray.700'} noOfLines={1}>
            {label}
        </Text>
        {hasChevron && (
            <Icon
                as={TbChevronDown as React.ElementType}
                boxSize={3.5}
                color="gray.400"
                flexShrink={0}
                opacity={0}
                transition="opacity 0.15s"
                _groupHover={{ opacity: 1 }}
            />
        )}
    </HStack>
);

export interface InlineSelectCellProps {
    value: string;
    options: InlineCellOption[];
    onSelect: (value: string) => void;
    isDisabled?: boolean;
    ariaLabel: string;
    /** Shown when the value matches no option. */
    placeholder?: string;
    searchPlaceholder?: string;
    minW?: string;
    /** Pass 'bottom-end' for cells near the right edge; see SettingsInlineEdit. */
    placement?: 'bottom-start' | 'bottom-end';
}

/**
 * Single-select cell editor: click the cell, pick an option, saved right away.
 * Long option lists get a search box, matching the role picker in the design.
 */
export const InlineSelectCell = ({
    value,
    options,
    onSelect,
    isDisabled = false,
    ariaLabel,
    placeholder = '—',
    searchPlaceholder = 'Search',
    minW = '200px',
    placement = 'bottom-start',
}: InlineSelectCellProps) => {
    const [search, setSearch] = useState('');
    const selected = options.find((option) => option.value === value);
    const showSearch = options.length > SEARCH_THRESHOLD;

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.label.toLowerCase().includes(query));
    }, [options, search]);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={selected?.label ?? placeholder}
                    isMuted={!selected}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW={minW}
            placement={placement}
            onOpen={() => setSearch('')}
        >
            {(close) => (
                <VStack align="stretch" spacing={showSearch ? 2 : 0}>
                    {showSearch && (
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={TbSearch as React.ElementType} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                autoFocus
                                value={search}
                                placeholder={searchPlaceholder}
                                onChange={(e) => setSearch(e.target.value)}
                                borderRadius="md"
                            />
                        </InputGroup>
                    )}
                    <VStack align="stretch" spacing={0} maxH="240px" overflowY="auto">
                        {visibleOptions.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" px={3} py={2}>
                                No matches
                            </Text>
                        ) : (
                            visibleOptions.map((option) => {
                                const isSelected = option.value === value;
                                return (
                                    <SettingsOptionRow
                                        key={option.value}
                                        label={option.label}
                                        isSelected={isSelected}
                                        onToggle={() => {
                                            if (!isSelected) onSelect(option.value);
                                            close();
                                        }}
                                    />
                                );
                            })
                        )}
                    </VStack>
                </VStack>
            )}
        </SettingsInlineEdit>
    );
};

/** Assignable locations: real shops only, plus the org-wide "all" sentinel. */
export const selectableLocations = (locations: LocationEntry[]): LocationEntry[] =>
    filterLocations(locations, { excludeAll: true, activeOnly: true });

/** "All Locations", one shop's name, or a count once several are assigned. */
export const formatLocations = (
    value: Array<string> | 'all',
    locations: LocationEntry[],
): string => {
    if (value === 'all') return 'All Locations';
    if (value.length === 0) return 'None';
    if (value.length === 1) {
        return locations.find((location) => location._id === value[0])?.title ?? '1 location';
    }
    return `${value.length} locations`;
};

/** Order-insensitive compare of two location assignments. */
export const isSameLocationValue = (
    a: Array<string> | 'all',
    b: Array<string> | 'all',
): boolean => {
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((id) => other.has(id));
};

export interface LocationsPickerProps {
    value: Array<string> | 'all';
    locations: LocationEntry[];
    onChange: (next: Array<string> | 'all') => void;
    maxH?: string;
}

/**
 * All Locations toggle over a checkbox list of shops, with search once the org
 * has more than a handful. Shared by the inline cell and the bulk editors.
 *
 * Checkboxes rather than the tinted option rows the single-select cells use:
 * locations are ticked in sets, and All Locations means every shop, so a whole
 * panel of filled rows is unreadable at this length.
 */
export const LocationsPicker = ({
    value,
    locations,
    onChange,
    maxH = '220px',
}: LocationsPickerProps) => {
    const [search, setSearch] = useState('');
    const options = useMemo(() => selectableLocations(locations), [locations]);
    const showSearch = options.length > SEARCH_THRESHOLD;

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.title.toLowerCase().includes(query));
    }, [options, search]);

    const isAll = value === 'all';
    const selectedIds = isAll ? new Set<string>() : new Set(value);

    const toggle = (id: string, checked: boolean) => {
        // Leaving "All Locations" starts from every shop, so unchecking one
        // reads the way it looks rather than wiping the assignment.
        const base = isAll ? options.map((option) => option._id as string) : Array.from(selectedIds);
        const next = checked
            ? Array.from(new Set([...base, id]))
            : base.filter((current) => current !== id);
        onChange(next);
    };

    return (
        <VStack align="stretch" spacing={2}>
            {showSearch && (
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        value={search}
                        placeholder="Search locations"
                        onChange={(e) => setSearch(e.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>
            )}
            <Checkbox
                size="sm"
                colorScheme="partbay-blue"
                isChecked={isAll}
                isIndeterminate={!isAll && selectedIds.size > 0}
                onChange={(e) => onChange(e.target.checked ? 'all' : [])}
            >
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    All Locations
                </Text>
            </Checkbox>
            <Divider />
            <VStack align="stretch" spacing={1} maxH={maxH} overflowY="auto">
                {visibleOptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={1} py={1}>
                        No matches
                    </Text>
                ) : (
                    visibleOptions.map((option) => {
                        const id = option._id as string;
                        const isSelected = isAll || selectedIds.has(id);
                        return (
                            <Checkbox
                                key={id}
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={isSelected}
                                onChange={(e) => toggle(id, e.target.checked)}
                            >
                                <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                    {option.title}
                                </Text>
                            </Checkbox>
                        );
                    })
                )}
            </VStack>
        </VStack>
    );
};

export interface InlineLocationsCellProps {
    value: Array<string> | 'all';
    locations: LocationEntry[];
    onSave: (next: Array<string> | 'all') => void;
    isDisabled?: boolean;
}

/**
 * Locations cell editor. Unlike the single-selects it saves on close, since
 * picking several shops takes more than one click. An empty selection is
 * treated as no change — a user has to belong somewhere.
 */
export const InlineLocationsCell = ({
    value,
    locations,
    onSave,
    isDisabled = false,
}: InlineLocationsCellProps) => {
    const [draft, setDraft] = useState<Array<string> | 'all'>(value);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatLocations(value, locations)}
                    isMuted={value !== 'all' && value.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel="Edit locations"
            isDisabled={isDisabled}
            minW="260px"
            onOpen={() => setDraft(value)}
            onClose={() => {
                if (draft !== 'all' && draft.length === 0) return;
                if (!isSameLocationValue(draft, value)) onSave(draft);
            }}
        >
            <Box>
                <LocationsPicker value={draft} locations={locations} onChange={setDraft} />
                {draft !== 'all' && draft.length === 0 && (
                    <Flex pt={2}>
                        <Text fontSize="xs" color="red.500">
                            Pick at least one location.
                        </Text>
                    </Flex>
                )}
            </Box>
        </SettingsInlineEdit>
    );
};
